import React from 'react';
import type { DASTicketDetails } from '../../../../api/types/dasTickets.types';
import { CompletedTicketModal } from './CompletedTicketModal';
import { ActiveTicketModal } from './ActiveTicketModal';

interface TicketModalRouterProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: DASTicketDetails;
  onTakeAction: () => void;
  onResubmit?: () => void;
  onCancel?: () => void;
}

/**
 * TicketModalRouter
 *
 * Routes to the appropriate modal based on ticket status:
 * - CompletedTicketModal: For archived/approved/rejected/cancelled tickets (read-only documentation view)
 * - ActiveTicketModal: For active/pending tickets (interactive view with action capabilities)
 */
export const TicketModalRouter: React.FC<TicketModalRouterProps> = ({
  isOpen,
  onClose,
  ticket,
  onTakeAction,
  onResubmit,
  onCancel,
}) => {
  // Final/closed stages that should show the read-only completed modal
  const closedStages = [
    'Approved',
    'Rejected',
    'Cancelled',
    'Closed - Approved via Consolidation',
    'Closed - Rejected via Consolidation',
  ];

  // Determine if ticket is in a final/closed state
  // Also check if currentStage starts with "Closed" to catch any other closed variations
  const isCompleted =
    ticket.status === 'Archived' ||
    closedStages.includes(ticket.currentStage) ||
    ticket.currentStage.toLowerCase().startsWith('closed');

  // Route to appropriate modal
  if (isCompleted) {
    return <CompletedTicketModal isOpen={isOpen} onClose={onClose} ticket={ticket} />;
  }

  return (
    <ActiveTicketModal
      isOpen={isOpen}
      onClose={onClose}
      ticket={ticket}
      onTakeAction={onTakeAction}
      onResubmit={onResubmit}
      onCancel={onCancel}
    />
  );
};

export default TicketModalRouter;
